import React, { useEffect, useState } from 'react';
import {
  Button,
  NEW_CART_PAGE,
  OLD_CART_PAGE,
  globals,
  useCheckoutRedirect,
  Link,
  smallProductImageUrl,
  ProductImage,
  handleDLMiniCartClick,
  BottleCount,
  getDefaultImageSrc,
  productLinkBuilder,
} from 'common-ui';
import { formatCurrency } from 'common-ui/src/utils/formatCurrency';
import type { LineItem, MiniCartLayoutProps } from 'tsconfig/types';
import {
  getGiftAndDepositeCardImage,
  handleMiniCartClickEvents,
  handleRedirectionClick,
} from '../../../utils/cart/miniCartUtils';
import ViewCart from '../components/ViewCart';
import GiftMessage from '../components/GiftMessage';
import { checkIsUnlimitedItem } from '../../../utils/cart/cartUtils';

const MiniCartLayout: React.FC<MiniCartLayoutProps> = ({
  lineItems,
  totalLineItem,
  onClose,
  isFreeGiftLineItem,
  isGreatPick,
  qty,
  showCheckoutButton,
}) => {
  const redirectCheckoutRoute = useCheckoutRedirect();
  const isUK = globals.country === 'uk';

  const [imageSrc, setImageSrc] = useState<string>(getDefaultImageSrc(lineItems));
  const isMemberPrice =
    (globals?.useNewCartAPI && lineItems[0]?.unitPrice?.priceType?.toLowerCase() === 'mpp') ||
    lineItems[0]?.unitPrice?.priceType === 'MemberPrice Discount';

  const redirectLink = globals?.useNewCartAPI ? NEW_CART_PAGE : OLD_CART_PAGE;

  const productHref = productLinkBuilder(lineItems[0]?.product?.productSlug, lineItems[0]?.sku?.itemCode);

  useEffect(() => {
    const getProductImageUrl = smallProductImageUrl;
    let newImageSrc = getProductImageUrl(lineItems[0]?.product?.itemCode ?? '');

    if (!isUK) {
      const productType = lineItems[0]?.product?.productType.toLowerCase();

      if (globals.country === 'au' && checkIsUnlimitedItem(lineItems)) {
        newImageSrc = `${globals.publicHost}/images/au/common/logos/unlimited_logo.svg`;
      } else if (productType === 'voucher') {
        newImageSrc = getGiftAndDepositeCardImage((globals?.brandId as string) ?? '', 'GIFT');
      } else if (productType === 'service' && lineItems[0]?.itemClassifications?.includes('DEPOSIT_ITEM')) {
        const depositInfo = lineItems[0]?.lineItemIdentification?.depositInformation;

        if (depositInfo?.depositType === 'PRESELL') {
          newImageSrc = getProductImageUrl(depositInfo?.associatedItemCode ?? '');
        } else {
          newImageSrc = getGiftAndDepositeCardImage((globals?.brandId as string) ?? '', 'DEPOSIT');
        }
      }
    }

    setImageSrc(newImageSrc);
  }, [lineItems]);

  useEffect(() => {
    handleDLMiniCartClick('launchMiniCart', 'launchMiniCart');
  }, []);

  const handleContinueShopping = () => {
    onClose();
  };

  const getStrikethroughPrice = (lineItem: LineItem, itemQty: number) => {
    const hasDiscountSavings = lineItem?.savings?.totalProductDiscountSavings > 0;
    const listPrice = lineItem?.unitPrice?.listPrice?.amount;
    const currentPrice = lineItem?.unitPrice?.value?.amount;

    if (hasDiscountSavings && listPrice * itemQty !== currentPrice) {
      return <h6 className="price strike">{formatCurrency(listPrice * itemQty)}</h6>;
    }

    return null;
  };

  const renderMemberSavings = () => {
    if (!isMemberPrice) {
      return null;
    }

    const { brandId } = globals;

    const memberSavings = isUK
      ? (lineItems[0]?.savings?.totalProductDiscountSavings / Number(lineItems[0]?.quantity)) * Number(qty)
      : lineItems[0]?.savings?.totalProductDiscountSavings;

    const label = brandId === 'wsj' && !isUK ? 'ADVANTAGE' : 'UNLIMITED';

    return (
      <p className="member-savings">
        {formatCurrency(memberSavings)} {label} MEMBER SAVINGS
      </p>
    );
  };

  // Mini cart price calculation logic:
  // If the country is UK or useNewCartAPI is enable, the total price is calculated as lineItem.unitPrice.value.amount * itemQty
  // If the country is not UK, the total price is taken from lineItem.unitPrice.value.amount
  const getPrice = (lineItem: LineItem, itemQty: number) => {
    const { useNewCartAPI } = globals;
    if (isUK || useNewCartAPI) {
      return formatCurrency(lineItem?.unitPrice?.value?.amount * itemQty);
    }
    return formatCurrency(lineItem?.unitPrice?.value?.amount);
  };

  // Logic behind this is, if the country is UK, then the itemQty should be taken from the qty prop.
  // If the country is not UK, then the itemQty should be taken from the lineItems prop.
  const itemQty = isUK ? Number(qty) : Number(lineItems[0]?.quantity);

  return (
    <>
      <div className="wrapper">
        <div className="product-image">
          <ProductImage
            src={imageSrc}
            productType={lineItems[0]?.product?.mixed ? 'Case' : lineItems[0]?.product?.productType}
            alt={lineItems[0]?.sku?.productName[0]?.value || ''}
            fill={true}
          />
        </div>
        <div className="product-details-container">
          {globals.brandId !== 'amx' && (
            <h5 className="product-great-pick lower">{isGreatPick ? 'Another Great Pick!' : 'Great Pick!'}</h5>
          )}
          <div className="product-detail-text">
            {lineItems[0]?.product?.productType?.toLowerCase() === 'service' ? (
              <h5 className="product-link lower">
                {lineItems[0]?.sku?.productName[0]?.value} {lineItems[0]?.product?.vintage || ''}
              </h5>
            ) : (
              <Link
                href={productHref}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  handleDLMiniCartClick(
                    'miniCartClick',
                    'miniCartClickPDP',
                    lineItems[0]?.sku?.productName[0]?.value +
                      (lineItems[0]?.product?.vintage ? ` ${lineItems[0]?.product?.vintage}` : ''),
                  );
                  handleRedirectionClick(e, productHref, onClose);
                }}
              >
                <h5 className="product-link lower">
                  <span className="checkout-item-qty">{itemQty}× </span>
                  {lineItems[0]?.sku?.productName[0]?.value} {lineItems[0]?.product?.vintage || ''}
                </h5>
              </Link>
            )}
          </div>
          <BottleCount
            productType={
              lineItems[0]?.product?.skus?.[0]?.referencedSkus?.[0]?.expandedItem?.productType
                ? lineItems[0]?.product?.skus?.[0]?.referencedSkus?.[0]?.expandedItem?.productType
                : lineItems[0]?.product?.productType
            }
            bottleType={lineItems[0]?.product?.bottleType}
            numberOfBottles={lineItems[0]?.sku?.numberOfBottles}
          />
          <div className="price-section">
            {getStrikethroughPrice(lineItems[0], itemQty)}
            <h6 className={`price sale-price ${isMemberPrice ? 'member-price' : ''}`}>
              {getPrice(lineItems[0], itemQty)}
            </h6>
          </div>
          {renderMemberSavings()}
          {isFreeGiftLineItem && <GiftMessage className="gift-tablet" redirectLink={redirectLink} onClose={onClose} />}
        </div>
      </div>
      {isFreeGiftLineItem && (
        <GiftMessage className="gift-other-device" redirectLink={redirectLink} onClose={onClose} />
      )}
      <div className="button-container">
        <Link href={globals?.useNewCartAPI ? NEW_CART_PAGE : OLD_CART_PAGE} prefetch={false}>
          <ViewCart totalLineItem={totalLineItem} redirectLink={redirectLink} onClose={onClose} isUK={isUK} />
        </Link>
        {showCheckoutButton && (
          <a href={redirectCheckoutRoute}>
            <Button
              kind="primary"
              data-testid="checkout-button"
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                handleRedirectionClick(e, redirectCheckoutRoute, onClose);
                handleMiniCartClickEvents('checkoutMiniCartClick');
              }}
            >
              CHECKOUT
            </Button>
          </a>
        )}
      </div>

      <div
        className="continue-shopping mobile"
        onClick={() => {
          handleContinueShopping();
          handleMiniCartClickEvents('continueShoppingtMiniCartClick');
        }}
        tabIndex={0}
        role="button"
        onKeyDown={() => {
          handleContinueShopping();
          handleMiniCartClickEvents('continueShoppingtMiniCartClick');
        }}
      >
        <span className="continue-shopping-text">continue shopping</span>
      </div>
    </>
  );
};

export default MiniCartLayout;
